import { useEffect, useState } from "react"
import { AppDispatch, RootState } from "../../../shared/store"
import { useDispatch, useSelector  } from 'react-redux'
import * as mapComponents from "../map-components"
import "./MainMapScreen.scss"
import { setBoundingBox, fetchMapLayers, fetchMapLayerData, updateFilterValue } from "../map-slice"
import { ICoordinates, IMapData, IMapLayer } from "../../../shared/models/map_models"
import { Loader } from "../../../shared/components"
import { GeoJSONFeature, IMapFilter, IMapLocation, ISelectedMapPoint, MapQueryParams } from "../map-models"
import { Feature } from 'geojson'
import * as paramUtilities from '../param-state-utility'
import { useLocalStorageWatcher } from "../../../shared/components/localStorageWatcher"
import { UserSettings } from "../../../shared/constants/user_constants"

const MainMapScreen = () => {

    const mapSlice = useSelector((state: RootState) => state.map)
    const appSlice = useSelector((state: RootState) => state.app)
    const dispatch: AppDispatch = useDispatch();

    const [showSideBar, setShowSideBar] = useState(false)
    const [mapLoaded, setMapLoaded] = useState(false)
    const [selectedMapPoint, setSelectedMapPoint] = useState<ISelectedMapPoint | null>(null)
    const [activeMapLayer, setActiveMapLayer] = useState<IMapLayer | null>(null)
    const [listFeatures, setListFeatures] = useState<Array<Feature> | null>(null)
    const [listLayer, setListLayer] = useState<IMapLayer | null>(null)
    const [loadOnMove, setLoadOnMove] = useState(true)
    const [mapLocation, setMapLocation] = useState<IMapLocation | null>(null)
    const [disableListView, setDisableListView] = useState<boolean>(false)
    const [minimizeList, setMinimizeList] = useState(false)

    useEffect(() => {
        if(!mapSlice.mapLayers){
            dispatch(fetchMapLayers())
        }
    }, [])

    useEffect(() => {
        let disableListViewValue = localStorage.getItem(UserSettings.SHOW_MAP_LIST)
        if (typeof disableListViewValue !== 'string'){
            setDisableListView(!appSlice.appFeatureConfigs?.map.list_view || false)
        }
        else
            setDisableListView(!(disableListViewValue === 'true'))


        if (!appSlice.appFeatureConfigs?.map.list_view){
            setDisableListView(true)
        }
    }, [appSlice.appFeatureConfigs])

    useEffect(() => {
        // Setting ActiveMapLayer and ListLayer on initial Load
        if(mapSlice.mapLayers){
            if(activeMapLayer === null){
                const activeLayerParam = paramUtilities.getQueryParam(MapQueryParams.ActiveLayer)
                var initialActiveLayer: IMapLayer | null = null
                if(activeLayerParam !== null){
                    const foundLayer = mapSlice.mapLayers.find(x => x.id === Number(activeLayerParam))
                    if (foundLayer !== undefined) initialActiveLayer = foundLayer
                }

                if (initialActiveLayer === null){
                    const firstVisibile = mapSlice.mapLayers.find((x) => x.visible === true)
                    if (firstVisibile !== undefined) {
                        initialActiveLayer = firstVisibile
                        paramUtilities.setQueryParam(MapQueryParams.ActiveLayer, String(firstVisibile.id))
                    }
                }
                if(initialActiveLayer !== null)
                    setActiveMapLayer(initialActiveLayer)

                if(!listLayer){
                    const listViewParam = paramUtilities.getQueryParam(MapQueryParams.ListViewLayer)
                    if (listViewParam){
                        const foundListViewLayer = mapSlice.mapLayers.find(x => x.id === Number(listViewParam))
                        if (foundListViewLayer) setListLayer(foundListViewLayer)
                    } else if(initialActiveLayer) {
                        setListLayer(initialActiveLayer)
                        paramUtilities.setQueryParam(MapQueryParams.ListViewLayer, String(initialActiveLayer.id))
                    }
                }

                handleLayerVisibility()
                
            } else {  // This updates when filters are clicked as they do not directly read from state
                const activeLayerParam = paramUtilities.getQueryParam(MapQueryParams.ActiveLayer)
                if (!activeLayerParam)
                    paramUtilities.setQueryParam(MapQueryParams.ActiveLayer, String(activeMapLayer.id))

                const listViewParam = paramUtilities.getQueryParam(MapQueryParams.ListViewLayer)
                if(!listViewParam)
                    paramUtilities.setQueryParam(MapQueryParams.ListViewLayer, String(listLayer?.id))

            }
        }
    }, [mapSlice.mapLayers])

    useEffect(() => {
        // Getting map data after map has loaded, the bounding box has been set, and the datasets are loaded
        if (mapLoaded && mapSlice?.boundingBox && mapSlice.boundingBox.length > 0 && mapSlice.mapLayers){
            if(loadOnMove)
                dispatch(fetchMapLayerData())
        }

    }, [mapSlice.boundingBox, mapLoaded, mapSlice.mapLayers, mapSlice.filters])

    useEffect(() => {
         handleListFeatureUpdate()
    }, [mapSlice.layerFeatureCollections])

    useLocalStorageWatcher(UserSettings.SHOW_MAP_LIST, (newValue) => {
        setDisableListView(!(newValue === 'true'))
    });

    const handleMapPointClick = (mapPoint: IMapData, layerId: number) => {
        const selectedLayer = mapSlice.mapLayers?.find((x) => x.id === layerId)
        if(selectedLayer){
            setSelectedMapPoint({point: mapPoint, layer: selectedLayer})
            setShowSideBar(true)
        }
    }

    const updateFilter = (filter: IMapFilter) => {
        dispatch(updateFilterValue(filter))
    }

    const handleLayerVisibility = (layerId?: number) => {
        paramUtilities.handleLayerState(mapSlice.mapLayers || [], dispatch, layerId)
        handleListFeatureUpdate()
    }

    const handleActiveMapLayerChange = (layer: IMapLayer) => {
        setActiveMapLayer(layer)
        paramUtilities.setQueryParam(MapQueryParams.ActiveLayer, String(layer.id))
    }
    
    const handleFeatureCollectionSelected = (
        features:  Array<Feature>,
        layer: IMapLayer
    ) => {
        setListFeatures(features)
        setListLayer(layer)
        handleActiveMapLayerChange(layer)
    }

    const handleMapCardSelected = (mapData: IMapData, layer: IMapLayer, index: number, goTo: boolean) => {
        if (!selectedMapPoint || selectedMapPoint.index !== index){
            setSelectedMapPoint({point: mapData, layer: layer, index: index})
            setShowSideBar(true)
            setLoadOnMove(false)

            if (goTo) {
                const foundFeatureLayer = mapSlice.layerFeatureCollections?.find((x) => x.layerId === layer.id)
                if (foundFeatureLayer){
                    const foundFeature = foundFeatureLayer.features[index] as GeoJSONFeature | null
                    if (foundFeature){
                        const lng = foundFeature.geometry.coordinates[0]
                        const lat = foundFeature.geometry.coordinates[1]
                        setMapLocation({lng, lat, zoom: 12, xOffset: 100})
                    }
                }
            }
        } else {
            deselectMapPoint()
        }
    }

    const deselectMapPoint = () => {
        setSelectedMapPoint(null)
        setShowSideBar(false)
        setLoadOnMove(true)
        setMapLocation(null)
    }

    const handleListViewChange = (layerId: number) => {
        const foundLayer = mapSlice.mapLayers?.find((x) => x.id === layerId)
        if (foundLayer){ 
            setListLayer(foundLayer)
            paramUtilities.setQueryParam(MapQueryParams.ListViewLayer, String(layerId))
            const foundLayerFeatures = mapSlice.layerFeatureCollections?.find((x) => x.layerId === foundLayer.id)
            if(foundLayerFeatures) setListFeatures(foundLayerFeatures.features)
        }
    }

    const handleListFeatureUpdate = () => {
        if (mapLoaded && mapSlice.mapLayers && listLayer){
            const foundFeatures = mapSlice.layerFeatureCollections?.find(x => x.layerId === listLayer.id)
            const foundListLayer = mapSlice.mapLayers.find((x) => x.id === listLayer.id)

            if (foundFeatures && foundListLayer?.visible){
                setListFeatures(foundFeatures.features)
            }
            else if(!foundListLayer?.visible){
                setListFeatures(null)
            } 

            if (foundListLayer && (listLayer.visible !== foundListLayer?.visible)){
                setListLayer(foundListLayer)
            }
         }
    }

    const toggleMinimizeList = () => {
        setMinimizeList(!minimizeList)
    }

    const getSideModalOffset = () => {
        
        if(!appSlice.appFeatureConfigs?.map?.list_view){
            return 'none'
        } else {

            if(disableListView)
                return 'none'
            
            if (minimizeList)
                return 'small'
            else 
                return 'large'
        }
    }

    return (
        <> 
            {!!selectedMapPoint && 
                <mapComponents.MapSideModal 
                    showSideModal={showSideBar} 
                    selectedPointData={selectedMapPoint}
                    onClose={deselectMapPoint}
                    offSetSize={getSideModalOffset()}
                />
            }
            {mapSlice.mapLayers ? 
                <div className="main-map-container">
                    <div className="map-screen-list-wrapper">
                        {appSlice.appFeatureConfigs?.map.list_view && !disableListView &&
                            <mapComponents.ListView 
                                features={listFeatures || []} 
                                layer={listLayer}
                                loading={mapSlice.loading || !mapLoaded}
                                cardSelected={handleMapCardSelected}
                                activeCardIndex={selectedMapPoint?.index}
                                listViewOptions={mapSlice.mapLayers}
                                listViewSelected={handleListViewChange}
                                minimizeList={minimizeList}
                                toggleMinimize={toggleMinimizeList}
                            />
                        }
                    </div>
                    <div className={"map-content-wrapper" + (!disableListView ? ' list-view-active':'')}>
                        <div className="topbar-wrapper hide-scrollbar">
                            {activeMapLayer &&
                                <>
                                   {mapSlice.mapLayers.length > 1 ?
                                        <mapComponents.MapLayerSelector 
                                            layers={mapSlice.mapLayers || []}
                                            activeLayer={activeMapLayer}
                                            setActiveLayer={handleActiveMapLayerChange}
                                            setLayerVisible={handleLayerVisibility}
                                        />
                                        :
                                        <span></span>
                                    }
                                    <mapComponents.MapFilters 
                                        layerId={activeMapLayer.id}
                                        filters={mapSlice.filters || []}
                                        updateFilter={updateFilter}
                                    />
                                </>
                            }
                        </div>
                        <div className="map-component-wrapper">
                            <mapComponents.MapBoxMapComponent 
                                mapLayers={mapSlice.mapLayers}
                                mapLoadded={() => setMapLoaded(true)}
                                mapLayerData={mapSlice.mapLayerData || []}
                                selectMapPoint={handleMapPointClick}
                                featureCollectionSelected={handleFeatureCollectionSelected}
                                fetchData={(boundingBox: Array<ICoordinates>) =>  dispatch(setBoundingBox(boundingBox))}
                                loading={mapSlice.loading}
                                mapLocation={mapLocation}
                                mapSizeChanged={minimizeList}
                            />
                        </div>
                    </div>
                </div>
            :
                <Loader />
            }
        </>
    )
}

export default MainMapScreen